import { MessageDescriptor } from 'react-intl';

import { IDayOfTheWeeks } from '@model';

import { FIRST_MONTH_IN_YEAR_VALUE, LAST_MONTH_IN_YEAR_VALUE } from './constants';
import { getTodayDate } from './dateProvider';

/**
 * Date type object can be string due to automatic conversion in TSX we need to convert to Date again.
 * @param date The date value (e.g. in runtime can be string)
 */
export const ensureObjectIsDate = (date: Date): Date => new Date(date);

/**
 * Gets date without time values (e.g. 0:00:00)
 * @param dateTime The date to apply
 */
export const getDateWithoutTime = (dateTime: Date): Date => {
  const date = new Date(dateTime);
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * Check is date is within range, return true if it is in range
 * @param date Date that is being examined
 * @param startDate starting date of the range
 * @param endDate ending date of the range
 */
export const dateInRange = (date: Date, startDate: Date, endDate: Date): boolean => {
  const prepDate = getDateWithoutTime(ensureObjectIsDate(date));

  return (
    getDateWithoutTime(ensureObjectIsDate(startDate)) <= prepDate &&
    getDateWithoutTime(ensureObjectIsDate(endDate)) >= prepDate
  );
};

/**
 * Indicates whether date is tomorrow.
 * @param dateToTest The date to test.
 */
export const isTomorrow = (dateToTest: Date): boolean =>
  getDateWithoutTime(getTomorrowsDate()).getTime() === getDateWithoutTime(ensureObjectIsDate(dateToTest)).getTime();

/**
 * Generated tomorrow's date
 */
export const getTomorrowsDate = (): Date => {
  const todayDate = getTodayDate();
  return new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() + 1);
};

/**
 * Method to check if the given date is valid or not.
 * @param date - date to be checked.
 */
export const isDateValid = (date: Date): boolean => !isNaN(date.getTime());

/**
 * Check whether the date is overdue.
 * Date is overdue if today's date has higher value than passed date
 * @param date - Date to be checked
 */
export const isDateOverdue = (date: Date): boolean => {
  return getTodayDate() > date;
};

/**
 * Indicates whether date is today.
 * @param dateToTest Date to test
 * @returns
 */
export const isToday = (dateToTest: Date): boolean => {
  return getDateWithoutTime(getTodayDate()).getTime() === getDateWithoutTime(ensureObjectIsDate(dateToTest)).getTime();
};

/**
 * Creates date that is shifted with number of seconds.
 * @param timeShift - number of seconds that should be added tu NOW time
 */
export const createDateWithTimeShift = (timeShift: number): Date => {
  const now = getTodayDate();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds() + timeShift,
  );
};

export const isSameDay = (firstDate: Date, secondDate: Date): boolean =>
  ensureObjectIsDate(firstDate).getTime() === ensureObjectIsDate(secondDate).getTime();

/**
 * Get This month
 */
export const getThisMonth = (): number => getMonthFromDate(getTodayDate());

/**
 * Get month that follows current month
 */
export const getFollowingMonth = (): number => {
  const currentMonth = getThisMonth();

  if (currentMonth === LAST_MONTH_IN_YEAR_VALUE) {
    return FIRST_MONTH_IN_YEAR_VALUE;
  }

  return currentMonth + 1;
};

/**
 * By default, javascript returns month values starting from 0.
 * This method converts this behaviour to return proper calendar month value.
 * @example
 * ```ts
 * January = 1
 * February = 2
 * ...
 * November = 11
 * December = 12
 * ```
 * @param date - Date that will be used to obtain month
 */
export const getMonthFromDate = (date: Date): number => ensureObjectIsDate(date).getMonth() + 1;

export const getDayOfWeekMessage = (date: Date, messages: IDayOfTheWeeks): MessageDescriptor => {
  const dayOfWeek = ensureObjectIsDate(date).getDay();

  switch (dayOfWeek) {
    case 1:
      return messages.monday;

    case 2:
      return messages.tuesday;

    case 3:
      return messages.wednesday;

    case 4:
      return messages.thursday;

    case 5:
      return messages.friday;

    case 6:
      return messages.saturday;

    case 0:
      return messages.sunday;

    default:
      throw new Error(`Unsupported day of week (${dayOfWeek}).`);
  }
};
